import React, { useState } from 'react';
import { Card, Alert, Button, Typography, Space } from 'antd';
import { LogoutOutlined, ExclamationCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { trpc } from '../../lib/trpc';
import WithdrawModal from '../../components/company/WithdrawModal';
import { PageHeader, PageContainer, DevFilePathBadge } from '../../components/common';
import { HelpButton } from '../../components/help/HelpButton';
import { Navigate } from 'react-router-dom';

const { Paragraph, Text } = Typography;

const WithdrawPage: React.FC = () => {
  const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);

  // 退会資格の確認
  const { data: withdrawEligibility, isLoading: eligibilityLoading } = trpc.company.canWithdraw.useQuery();

  // 会社情報の取得
  const { data: company, isLoading: companyLoading } = trpc.company.getCurrentCompany.useQuery();

  const isLoading = eligibilityLoading || companyLoading;

  // 退会資格がない場合はダッシュボードにリダイレクト
  if (!isLoading && !withdrawEligibility?.canWithdraw) {
    return <Navigate to="/app/settings" replace />;
  }

  return (
    <PageContainer>
      <DevFilePathBadge filePath="client/src/pages/settings/WithdrawPage.tsx" />
      <PageHeader
        title="退会"
        subtitle="アカウントの退会手続きを行います"
        extra={<HelpButton topicId="subscription-cancel" size="middle" position="inline" />}
      />

      <Card loading={isLoading}>
        {withdrawEligibility?.canWithdraw && company && (
          <>
            <Alert
              message={
                <Space>
                  <WarningOutlined />
                  <Text strong>退会について重要なお知らせ</Text>
                </Space>
              }
              description={
                <div style={{ marginTop: 8 }}>
                  <Paragraph>
                    退会手続きを行うと、以下の影響があります。ご確認の上、お手続きください。
                  </Paragraph>
                  <ul style={{ paddingLeft: 20, marginBottom: 0 }}>
                    <li>
                      <Text strong>全ユーザーがログインできなくなります</Text>
                      <br />
                      <Text type="secondary">この会社に所属する全てのユーザーアカウントが即座に無効化されます</Text>
                    </li>
                    <li style={{ marginTop: 12 }}>
                      <Text strong>登録データは削除されます</Text>
                      <br />
                      <Text type="secondary">退会後、アカウントデータは削除されます</Text>
                    </li>
                    <li style={{ marginTop: 12 }}>
                      <Text strong>この操作は取り消せません</Text>
                      <br />
                      <Text type="secondary">再度利用する場合は、新規登録が必要になります</Text>
                    </li>
                  </ul>
                </div>
              }
              type="warning"
              showIcon={false}
              style={{ marginBottom: 24 }}
            />

            <Card
              title={
                <Space>
                  <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
                  <span>退会手続き</span>
                </Space>
              }
              styles={{
                header: { borderBottom: '1px solid #ffccc7', background: '#fff2f0' },
              }}
            >
              <Paragraph>
                退会手続きを開始するには、以下のボタンをクリックしてください。
                確認画面で会社名を入力することで、退会が確定します。
              </Paragraph>

              <div style={{ marginTop: 16 }}>
                <Button
                  type="primary"
                  danger
                  size="large"
                  icon={<LogoutOutlined />}
                  onClick={() => setWithdrawModalOpen(true)}
                >
                  退会手続きを開始
                </Button>
              </div>
            </Card>

            <WithdrawModal
              open={withdrawModalOpen}
              onClose={() => setWithdrawModalOpen(false)}
              companyName={company.name}
            />
          </>
        )}
      </Card>
    </PageContainer>
  );
};

export default WithdrawPage;
